/**
 * Global audio manager for the mobile app — white-label edition.
 *
 * Adds: client-side auto-reconnect (if the audio element pauses or ends
 * unexpectedly while the user intended to be listening, we reload the src
 * and resume playback). This layers on top of the backend proxy's own
 * reconnect loop and covers Cloudflare / edge / network hiccups.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LS_STATION_KEY = 'kioo_mobile_station';

const STATIC_FALLBACK = [
  {
    slug: 'kioo',
    name: 'Kioo Radio',
    short_name: 'Kioo',
    frequency: '98.1 FM',
    tagline: 'The Gift of Good News',
    country: 'Foya, Liberia',
    language: 'Kissi / English / French',
    logo_url:
      'https://customer-assets-cm19k8pv.emergentagent.net/job_dbf8fccb-0921-4831-b85d-23b248549992/artifacts/y687vkx5_KIOO%20RADIO.webp',
    colors: {
      primary: '#16a34a',
      primary_dark: '#065f46',
      accent: '#22c55e',
      background: '#ecfdf5',
      text_on_primary: '#ffffff',
    },
    proxy_url: '/api/stream/kioo',
    has_own_stream: true,
  },
];

const AudioCtx = createContext(null);

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const intendPlayingRef = useRef(false); // did the user press Play?
  const reconnectingRef = useRef(false);
  const reconnectDelayRef = useRef(1000);
  // Only treat a "waiting" / "stalled" event as a real drop if it lasts a
  // few seconds. Transient rebuffers on mobile networks are normal and
  // reconnecting on every one of them makes the stream feel choppier.
  const stallTimerRef = useRef(null);
  const STALL_MS = 4000;

  const [stations, setStations] = useState(STATIC_FALLBACK);
  const [stationSlug, setStationSlug] = useState(() => {
    try {
      return localStorage.getItem(LS_STATION_KEY) || 'kioo';
    } catch (_e) {
      return 'kioo';
    }
  });
  const [state, setState] = useState({
    isPlaying: false,
    isBuffering: false,
    error: null,
  });

  // Load station registry from backend
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/stations`);
        if (alive && Array.isArray(data?.stations) && data.stations.length) {
          setStations(data.stations);
        }
      } catch (_e) {
        /* keep static fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const currentStation = useMemo(
    () => stations.find((s) => s.slug === stationSlug) || stations[0],
    [stations, stationSlug]
  );

  const streamUrl = useMemo(
    () => `${BACKEND_URL}${currentStation?.proxy_url || '/api/stream/kioo'}`,
    [currentStation]
  );

  const scheduleReconnect = useCallback(() => {
    if (!intendPlayingRef.current) return;
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    const delay = Math.min(reconnectDelayRef.current, 8000);
    setState((s) => ({ ...s, isBuffering: true, error: null }));
    setTimeout(async () => {
      reconnectingRef.current = false;
      const el = audioRef.current;
      if (!el || !intendPlayingRef.current) return;
      try {
        el.src =
          streamUrl + (streamUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
        el.load();
        await el.play();
        reconnectDelayRef.current = 1000; // reset on success
      } catch (_e) {
        // exponential backoff (bounded)
        reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, 8000);
        scheduleReconnect();
      }
    }, delay);
  }, [streamUrl]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const clearStall = () => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };
    const armStall = () => {
      clearStall();
      stallTimerRef.current = setTimeout(() => {
        if (intendPlayingRef.current) scheduleReconnect();
      }, STALL_MS);
    };
    const onPlay = () => {
      clearStall();
      setState((s) => ({ ...s, isPlaying: true, error: null }));
    };
    const onPause = () => {
      setState((s) => ({ ...s, isPlaying: false }));
      // Unexpected pause = network drop / stream ended. Reconnect quickly.
      if (intendPlayingRef.current) scheduleReconnect();
    };
    const onEnded = () => {
      if (intendPlayingRef.current) scheduleReconnect();
    };
    const onWaiting = () => {
      setState((s) => ({ ...s, isBuffering: true }));
      if (intendPlayingRef.current) armStall();
    };
    const onPlaying = () => {
      clearStall();
      setState((s) => ({ ...s, isBuffering: false, isPlaying: true }));
    };
    const onStalled = () => {
      setState((s) => ({ ...s, isBuffering: true }));
      if (intendPlayingRef.current) armStall();
    };
    const onError = () => {
      setState({ isPlaying: false, isBuffering: false, error: 'stream_error' });
      if (intendPlayingRef.current) scheduleReconnect();
    };
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    el.addEventListener('waiting', onWaiting);
    el.addEventListener('playing', onPlaying);
    el.addEventListener('stalled', onStalled);
    el.addEventListener('error', onError);
    return () => {
      clearStall();
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
      el.removeEventListener('waiting', onWaiting);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('stalled', onStalled);
      el.removeEventListener('error', onError);
    };
  }, [scheduleReconnect]);

  const play = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    intendPlayingRef.current = true;
    reconnectDelayRef.current = 1000;
    setState((s) => ({ ...s, isBuffering: true, error: null }));
    try {
      // Only set src if it differs from what's already loaded — avoids a
      // double-connect that would otherwise cause an extra buffering cycle.
      if (el.src !== streamUrl) {
        el.src = streamUrl;
      }
      await el.play();
    } catch (_e) {
      setState({ isPlaying: false, isBuffering: false, error: 'blocked' });
      intendPlayingRef.current = false;
    }
  }, [streamUrl]);

  const pause = useCallback(() => {
    intendPlayingRef.current = false;
    const el = audioRef.current;
    if (el) el.pause();
  }, []);

  const reload = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    intendPlayingRef.current = true;
    try {
      el.pause();
      el.src =
        streamUrl + (streamUrl.includes('?') ? '&' : '?') + 't=' + Date.now();
      el.load();
      await el.play();
    } catch (_e) {
      /* ignore */
    }
  }, [streamUrl]);

  const switchStation = useCallback(
    async (slug) => {
      if (!slug || slug === stationSlug) return;
      const target = stations.find((s) => s.slug === slug);
      if (!target) return;
      setStationSlug(slug);
      try {
        localStorage.setItem(LS_STATION_KEY, slug);
      } catch (_e) {
        /* ignore */
      }
      const el = audioRef.current;
      if (!el) return;
      const wasPlaying = !el.paused || intendPlayingRef.current;
      try {
        el.pause();
      } catch (_e) {
        /* ignore */
      }
      if (wasPlaying) {
        intendPlayingRef.current = true;
        try {
          el.src = `${BACKEND_URL}${target.proxy_url}`;
          el.load();
          await el.play();
        } catch (_e) {
          setState({ isPlaying: false, isBuffering: false, error: 'blocked' });
        }
      }
    },
    [stations, stationSlug]
  );

  const api = useMemo(
    () => ({
      isPlaying: state.isPlaying,
      isBuffering: state.isBuffering,
      error: state.error,
      streamUrl,
      stations,
      currentStation,
      switchStation,
      play,
      pause,
      reload,
    }),
    [
      state.isPlaying,
      state.isBuffering,
      state.error,
      streamUrl,
      stations,
      currentStation,
      switchStation,
      play,
      pause,
      reload,
    ]
  );

  return (
    <AudioCtx.Provider value={api}>
      {children}
      <audio
        ref={audioRef}
        preload="auto"
        playsInline
        data-testid="mobile-audio-element"
        style={{ display: 'none' }}
      />
    </AudioCtx.Provider>
  );
};

export const useKiooAudio = () => {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error('useKiooAudio must be used within <AudioProvider>');
  return ctx;
};
